import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import {
  ProductLite,
  searchProducts,
  getProductsByIds,
} from "../../api/productSearchApi";

type Props = {
  label: string;
  helperText?: string;
  selectedIds: number[];
  onChange: (ids: number[]) => void;
};

export default function PromotionProductPicker({
  label,
  helperText,
  selectedIds,
  onChange,
}: Props) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<ProductLite[]>([]);
  const [selected, setSelected] = useState<ProductLite[]>([]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
  let alive = true;

  async function hydrateSelected() {
    if (!selectedIds.length) {
      setSelected([]);
      return;
    }

    try {
      const rows = await getProductsByIds(selectedIds);
      if (alive) setSelected(rows);
    } catch {
      if (alive) {
        setSelected(
          selectedIds.map((id) => ({
            id,
            name: `Product #${id}`,
            price: 0,
            image_url: null,
          }))
        );
      }
    }
  }

  hydrateSelected();

  return () => {
    alive = false;
  };
}, [selectedIds]);
  

  useEffect(() => {
    let alive = true;

    async function run() {
      if (query.trim().length < 2) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        const rows = await searchProducts(query);
        if (alive) setOptions(rows);
      } finally {
        if (alive) setLoading(false);
      }
    }

    const t = window.setTimeout(run, 250);

    return () => {
      alive = false;
      window.clearTimeout(t);
    };
  }, [query]);

  return (
    <Autocomplete
      multiple
      options={options}
      value={selected}
      loading={loading}
      onInputChange={(_, value) => setQuery(value)}
      onChange={(_, value) => {
        setSelected(value);
        onChange(value.map((p) => p.id));
      }}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterSelectedOptions
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={option.name}
            size="small"
          />
        ))
      }
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id} sx={{ gap: 1.5 }}>
          <Avatar src={option.image_url || undefined} sx={{ width: 32, height: 32 }} />
          <Box>
            <Typography fontWeight={700}>{option.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              Product #{option.id} · ${Number(option.price || 0).toFixed(2)}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}